import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography,
  Alert,
  Skeleton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { userService } from '../../services/userService';
import RoleBadge from '../Common/RoleBadge';
import UserForm from './UserForm';
import ProjectAssignmentDialog from './ProjectAssignmentDialog';
import ConfirmDialog from '../Common/ConfirmDialog';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#C62828',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #8E0000',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Dialogs
  const [openUserForm, setOpenUserForm] = useState(false);
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((user) => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userService.getAll();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err) {
      setError('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setEditMode(false);
    setOpenUserForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setOpenUserForm(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    try {
      await userService.delete(userToDelete.user_id);
      setSuccess(`User "${userToDelete.username}" deleted successfully`);
      loadUsers();
    } catch (err) {
      setError('Failed to delete user: ' + err.message);
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleAssignProjects = (user) => {
    setSelectedUser(user);
    setOpenProjectDialog(true);
  };

  const handleFormClose = (shouldReload) => {
    setOpenUserForm(false);
    setSelectedUser(null);
    setEditMode(false);
    if (shouldReload) {
      loadUsers();
    }
  };

  const handleProjectDialogClose = (shouldReload) => {
    setOpenProjectDialog(false);
    setSelectedUser(null);
    if (shouldReload) {
      loadUsers();
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #C62828 0%, #EF5350 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(198, 40, 40, 0.3)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AdminIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">User Management</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Manage users, roles, and project assignments
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
            }}
          >
            Create User
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search users"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Username or email..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ minWidth: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Role</InputLabel>
              <Select
                value={roleFilter}
                label="Filter by Role"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="PowerUser">Power User</MenuItem>
                <MenuItem value="SuperUser">Super User</MenuItem>
                <MenuItem value="User">User</MenuItem>
              </Select>
            </FormControl>
            {(searchTerm || roleFilter) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredUsers.length} of {users.length} users
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellStyle}>Username</TableCell>
                  <TableCell sx={headerCellStyle}>Email</TableCell>
                  <TableCell sx={headerCellStyle}>Role</TableCell>
                  <TableCell sx={headerCellStyle}>Manager</TableCell>
                  <TableCell sx={{ ...headerCellStyle, backgroundColor: '#1565C0', borderBottom: '3px solid #0D47A1' }}>Projects</TableCell>
                  <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user, index) => (
                    <TableRow
                      key={user.user_id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#FFEBEE',
                        '&:hover': { backgroundColor: '#FFCDD2' },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>
                      {user.manager_username || (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.project_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Assign Projects">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAssignProjects(user)}
                        >
                          <AssignmentIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(user)}
                          disabled={user.role === 'Admin'} // Prevent deleting admins
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {openUserForm && (
        <UserForm
          open={openUserForm}
          onClose={handleFormClose}
          user={selectedUser}
          editMode={editMode}
          onSuccess={(message) => setSuccess(message)}
          onError={(message) => setError(message)}
        />
      )}

      {openProjectDialog && selectedUser && (
        <ProjectAssignmentDialog
          open={openProjectDialog}
          onClose={handleProjectDialogClose}
          user={selectedUser}
          onSuccess={(message) => setSuccess(message)}
          onError={(message) => setError(message)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message="Are you sure you want to delete this user? This will remove all their project assignments."
        details={userToDelete ? [
          { label: 'Username', value: userToDelete.username },
          { label: 'Role', value: userToDelete.role },
        ] : []}
      />
    </Box>
  );
};

export default UserManagement;
